import React from 'react';
import { NavLink } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FastForwardOutlinedIcon from '@mui/icons-material/FastForwardOutlined';
import Tooltip from '@mui/material/Tooltip';
import styles from '@/pages/task-list/styles.less';
import { routes } from '@/app/routing/index';
import {
  Immediate,
  Legal,
  Main,
  Assets,
  Closing,
} from '@/pages/task-list/components/ModuleTips';
import { TASK_MODULES, TASK_STATUS } from '@/common/constants/constants';
import {
  useCurrentDecedentId,
  useCurrentDecedentSections,
} from '@/app/providers/DecedentProvider';
import { useTaskListQuery } from '@/entities/decedent';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

const TABS = [
  {
    titleKey: 'Immediate tasks',
    Tip: Immediate,
    link: `${routes.dashboard.taskList}/${TASK_MODULES.IMMEDIATE}`,
    tab: TASK_MODULES.IMMEDIATE,
  },
  {
    titleKey: 'Legal tasks',
    Tip: Legal,
    link: `${routes.dashboard.taskList}/${TASK_MODULES.LEGAL}`,
    tab: TASK_MODULES.LEGAL,
  },
  {
    titleKey: 'Main tasks',
    Tip: Main,
    link: `${routes.dashboard.taskList}/${TASK_MODULES.MAIN}`,
    tab: TASK_MODULES.MAIN,
  },
  {
    titleKey: 'Assets tasks',
    Tip: Assets,
    link: `${routes.dashboard.taskList}/${TASK_MODULES.ASSET}`,
    tab: TASK_MODULES.ASSET,
  },
  {
    titleKey: 'Closing tasks',
    Tip: Closing,
    link: `${routes.dashboard.taskList}/${TASK_MODULES.CLOSING}`,
    tab: TASK_MODULES.CLOSING,
  },
];

const STARTED_TASK_STATUSES = [TASK_STATUS.ONGOING, TASK_STATUS.COMPLETED];

const getStatus = (sections, module, disabled, closingStarted) => {
  const current = sections[module];
  if (!current || disabled) {
    return 'locked';
  }

  if (
    module === TASK_MODULES.CLOSING
      ? current.task?.passed
      : current.question?.done && current.task?.done
  ) {
    return 'completed';
  }

  if (current.task?.allowed) {
    if (module === TASK_MODULES.CLOSING) {
      return closingStarted ? 'ongoing' : 'open';
    } else {
      return 'ongoing';
    }
  }
  if (current.question?.allowed && !current.question?.passed) {
    return 'open';
  }
  return 'ongoing';
};

const statusIcon = {
  locked: LockOutlinedIcon,
  completed: CheckCircleIcon,
  ongoing: FastForwardOutlinedIcon,
  open: LockOpenOutlinedIcon,
};

const Tabs = () => {
  const { t } = useTranslation();
  const sections = useCurrentDecedentSections() || {};
  const decedentId = useCurrentDecedentId();
  const { data: closingTasks = [] } = useTaskListQuery(
    decedentId,
    TASK_MODULES.CLOSING,
    { enabled: !!decedentId && !sections?.[TASK_MODULES.CLOSING]?.disabled }
  );
  const closingStarted = closingTasks.some(({ status }) =>
    STARTED_TASK_STATUSES.includes(status)
  );
  return (
    <div className={styles.tabs}>
      {TABS.map(({ link, titleKey, Tip, tab }, ind) => {
        const disabled = !!sections?.[tab]?.disabled;
        const status = getStatus(sections, tab, disabled, closingStarted);
        const StatusIcon = statusIcon[status];
        return (
          <NavLink
            key={titleKey}
            className={cx(
              styles.tabsItem,
              disabled && styles['tabsItem-disabled']
            )}
            activeClassName={styles['tabsItem-active']}
            to={link}
            disabled={disabled}
            onClick={disabled ? (event) => event.preventDefault() : undefined}
          >
            <div className={styles.tabsItemText}>
              <span className={styles.tabsItemTextNum}>
                {t('Module {{index}}', { index: ind + 1 })}
              </span>
              <span className={styles.tabsItemTextTitle}>{t(titleKey)}</span>
            </div>
            <div
              className={cx(
                styles.tabsItemStatuses,
                styles[`tabsItemStatuses-${status}`]
              )}
            >
              <Tooltip title={<Tip />} placement="left-end">
                <InfoIcon />
              </Tooltip>
              <Tooltip
                title={
                  <div
                    style={{
                      color: 'black',
                      textTransform: 'capitalize',
                      fontSize: '16px',
                    }}
                  >
                    {t(status)}
                  </div>
                }
                placement="left-end"
              >
                <StatusIcon />
              </Tooltip>
            </div>
          </NavLink>
        );
      })}
    </div>
  );
};

export default Tabs;
